import {
  FC, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import {
  Control, Controller, UseFormSetValue, UseFormWatch,
} from 'react-hook-form';

import useGetMetric from 'hooks/useGetMetric';
import Button from 'components/Button';
import Select from 'components/Select';
import { Mixpanel } from 'mixpanel';
import useUser from 'api/hooks/useUser';
import MaskInput from 'components/MaskedInput';
import { unMaskInput } from 'utils/helpers';
import { IFeasibilityCheck } from 'features/FeasibilityCheck';
import { cn } from 'utils/cn';

interface Props {
  setActiveTab: (value: number) => void;
  setValue: UseFormSetValue<IFeasibilityCheck>;
  formWatch: UseFormWatch<IFeasibilityCheck>;
  control: Control<IFeasibilityCheck>;
  isPDF?: boolean;
}

const TechnicalFeasibility: FC<Props> = ({
  setActiveTab,
  setValue,
  formWatch,
  control,
  isPDF,
}) => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { ton } = useGetMetric();

  const economicData = formWatch('economic');
  const chemicalData = formWatch('chemical');

  const {
    productPackaging,
    numberFtl,
    timeDeliveries,
    expectedProductConsumption,
  } = formWatch('technical');

  const [isExpProductConsumptionChanged, setIsExpProductConsumptionChanged] = useState(false);

  useEffect(() => {
    setValue('technical.productBefore', economicData?.alkali);
    setValue('technical.product', economicData?.omya);
  }, [economicData?.omya, economicData.alkali]);

  useEffect(() => {
    let value = 0;

    if (Number(unMaskInput(expectedProductConsumption))) {
      value = Math.ceil(Number(unMaskInput(expectedProductConsumption)) / 25);
    }

    setValue('technical.numberFtl', value);
  }, [expectedProductConsumption]);

  useEffect(() => {
    let value = 0;

    if (numberFtl) {
      value = Math.ceil(360 / numberFtl);
    }

    setValue('technical.timeDeliveries', value);
  }, [numberFtl]);

  const productPackagingOptions = [
    {
      label: t('bulk'),
      value: t('bulk'),
    },
    {
      label: t('ibc'),
      value: t('ibc'),
    },
  ];

  const pumpTypeOptions = [
    {
      label: t('membrane'),
      value: t('membrane'),
    },
    {
      label: t('peristaltic'),
      value: t('peristaltic'),
    },
    {
      label: t('progressingCavity'),
      value: t('progressingCavity'),
    },
    {
      label: t('centrifugal'),
      value: t('centrifugal'),
    },
    {
      label: t('other'),
      value: t('other'),
    },
  ];

  const yesNoOptions = [
    {
      label: t('yes'),
      value: t('yes'),
    },
    {
      label: t('no_1'),
      value: t('no'),
    },
  ];

  const tankBottomOptions = [
    {
      label: t('flat'),
      value: t('flat'),
    },
    {
      label: t('inclined'),
      value: t('inclined'),
    },
  ];

  const productWithdrawelOptions = [
    {
      label: t('viaBottom'),
      value: t('viaBottom'),
    },
    {
      label: t('viaTop'),
      value: t('viaTop'),
    },
  ];

  const errorExpectedConsumptionHandle = useMemo(() => {
    const value = unMaskInput(expectedProductConsumption);

    if (Number(value) < 1) {
      return t('minimumPossibleValue') + ' is 1';
    }

    if (Number(value) > 1000000) {
      return t('maximumPossibleValue') + ' is 1\'000\'000';
    }

    return '';
  }, [expectedProductConsumption]);

  const handleNext = (nextStep: number) => () => {
    setActiveTab(nextStep);
    Mixpanel.track('Feasibility Check Step 5', {
      role: user?.role?.name,
      organization: user?.organization,
    });
  };

  const disabledState = useMemo(() => !productPackaging?.value, [productPackaging?.value]);

  useEffect(() => {
    setValue('technical.expectedProductConsumption',
      `${chemicalData?.expectedProductConsumption || 1}`,
    );
  }, [chemicalData?.expectedProductConsumption]);

  return (
    <form className="border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl">
      <div className="text-black text-xl font-bold mb-8">
        {t('technicalFeasibility')}
      </div>
      <div className="text-black text-lg font-bold mb-6">
        {t('informationOnCurrentSetup')}
      </div>
      <div className={cn(
        'grid gap-6 grid-cols-2 grid-rows-2 md:grid-cols-1 md:gap-2',
        { 'grid-cols-1': isPDF },
      )}>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid" data-tooltip-id="product-input">
          <Select
            disabled={true}
            value={economicData?.omya ? [economicData.omya] : []}
            options={economicData?.omya ? [economicData.omya] : []}
            placeholder={t('specify')}
            title={t('product')}
            onChange={() => {}}
          />
        </div>
        <Tooltip
          id="product-input"
          place="bottom-start"
          className="-mt-6"
          variant="light"
          noArrow
        >
          {t('updateProductText')}
        </Tooltip>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid">
          <MaskInput
            value={`${expectedProductConsumption}`}
            className="w-full"
            titleClassName="top-[-9px] left-[8px] bg-blue-gradient"
            title={`${t('expectedAnnualConsumption')} (${ton})`}
            onChange={(value) => {
              if (Number(value) !== Number(chemicalData?.expectedProductConsumption)) {
                setIsExpProductConsumptionChanged(true);
              } else {
                setIsExpProductConsumptionChanged(false);
              }

              setValue('technical.expectedProductConsumption', value);
            }}
            isBlueText={isExpProductConsumptionChanged}
            isGreyText={!isExpProductConsumptionChanged}
            isBlueBg={true}
            redBorder={errorExpectedConsumptionHandle.length > 0}
          />
          <div className="text-[#FF3B30] text-xs ml-1 mt-1 h-2">
            {errorExpectedConsumptionHandle.length > 0 && (
              <div> {errorExpectedConsumptionHandle}</div>
            )}
          </div>
        </div>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value ? [value] : []}
                options={productPackagingOptions}
                placeholder={t('specify')}
                title={t('productPackaging')}
                onChange={onChange}
              />
            )}
            name="technical.productPackaging"
          />
        </div>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value ? [value] : []}
                options={pumpTypeOptions}
                placeholder={t('specify')}
                title={t('pumpType')}
                onChange={onChange}
              />
            )}
            name="technical.pumpType"
          />
        </div>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value ? [
                  {
                    value: value?.value,
                    label: value?.value === t('no') ? t('no_1') : t('yes'),
                  },
                ] : []}
                options={yesNoOptions}
                placeholder={t('specify')}
                title={t('rentalPumpRequired')}
                onChange={onChange}
              />
            )}
            name="technical.rentalPump"
          />
        </div>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value ? [value] : []}
                options={yesNoOptions}
                placeholder={t('specify')}
                title={t('linesTwenty')}
                onChange={onChange}
              />
            )}
            name="technical.linesMore"
          />
        </div>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value ? [value] : []}
                options={yesNoOptions}
                placeholder={t('specify')}
                title={t('linesFifteen')}
                onChange={onChange}
              />
            )}
            name="technical.linesLess"
          />
        </div>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value ? [value] : []}
                options={yesNoOptions}
                placeholder={t('specify')}
                title={t('valves')}
                onChange={onChange}
              />
            )}
            name="technical.valves"
          />
        </div>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value ? [value] : []}
                options={yesNoOptions}
                placeholder={t('specify')}
                title={t('riskExposure')}
                onChange={onChange}
              />
            )}
            name="technical.risk"
          />
        </div>
        <div className="w-full mb-1 md:mb-4 break-inside-avoid">
          <Select
            disabled={true}
            value={economicData?.alkali ? [economicData.alkali] : []}
            options={economicData?.alkali ? [economicData.alkali] : []}
            placeholder={t('specify')}
            title={t('productUsedBefore')}
            onChange={() => {}}
          />
        </div>
        {productPackaging?.value === t('bulk') && (
          <>
            <div className="w-full col-divider h-px bg-[#E2E2E2] mb-1" />
            <div className="w-full mb-1 md:mb-4 break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value ? [value] : []}
                    options={tankBottomOptions}
                    placeholder={t('specify')}
                    title={t('tankBottom')}
                    onChange={onChange}
                  />
                )}
                name="technical.tankBottom"
              />
            </div>
            <div className="w-full mb-1 md:mb-4 break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value ? [value] : []}
                    options={yesNoOptions}
                    placeholder={t('specify')}
                    title={t('stirredTank')}
                    onChange={onChange}
                  />
                )}
                name="technical.stirredTank"
              />
            </div>
            <div className="w-full mb-1 md:mb-4 break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value ? [value] : []}
                    options={yesNoOptions}
                    placeholder={t('specify')}
                    title={t('productRecirculation')}
                    onChange={onChange}
                  />
                )}
                name="technical.productRecirculation"
              />
            </div>
            <div className="w-full mb-1 md:mb-4 break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value ? [value] : []}
                    options={productWithdrawelOptions}
                    placeholder={t('specify')}
                    title={t('productWithdrawel')}
                    onChange={onChange}
                  />
                )}
                name="technical.productWithdrawel"
              />
            </div>
            <div className="w-full mb-1 md:mb-4 break-inside-avoid">
              <MaskInput
                disabled
                value={`${numberFtl || ''}`}
                className="w-full"
                titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
                title={t('numberOfFtl')}
                isGreyText
              />
            </div>
            <div className="w-full mb-1 md:mb-4 break-inside-avoid">
              <MaskInput
                disabled
                value={`${timeDeliveries || ''}`}
                className="w-full"
                titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
                title={`${t('timeBetweenDeliveries')}`}
                isGreyText
              />
            </div>
          </>
        )}
      </div>
      {!isPDF && (
        <div className="mt-8 flex justify-center md:mt-6">
          <Button
            onClick={() => setActiveTab(4)}
            className="mr-4"
            btnType="secondary"
          >
            {t('back')}
          </Button>
          <Button disabled={disabledState} onClick={handleNext(6)}>
            {t('next')}
          </Button>
        </div>
      )}
    </form>
  );
};

export default TechnicalFeasibility;
